import type {GlobalThemeOverrides} from 'naive-ui';
import './assets/scss/variables/_variables.css';

const cssVar = (val: string) => {
    return getComputedStyle(document.body).getPropertyValue(val);
}

export const districarThemeOverrides: GlobalThemeOverrides = {
    common: {
        // Color
        baseColor: cssVar('--dis-color-surface-primary'),
        bodyColor: cssVar('--dis-color-surface-base'),
        inputColor: cssVar('--dis-color-surface-secondary'),
        inputColorDisabled: cssVar('--dis-color-surface-disabled'),
        primaryColor: cssVar('--dis-color-action-primary'),
        primaryColorHover: cssVar('--dis-color-action-primary-hover'),
        primaryColorPressed: cssVar('--dis-color-action-primary-pressed'),
        infoColor: cssVar('--dis-color-surface-info'),
        infoColorHover: cssVar('--dis-color-surface-info-hover'),
        infoColorPressed: cssVar('--dis-color-surface-info-pressed'),
        successColor: cssVar('--dis-color-surface-success'),
        successColorHover: cssVar('--dis-color-surface-success-hover'),
        successColorPressed: cssVar('--dis-color-surface-success-pressed'),
        warningColorHover: cssVar('--dis-color-surface-warning-hover'),
        warningColorPressed: cssVar('--dis-color-surface-warning-pressed'),
        errorColorHover: cssVar('--dis-color-surface-error-hover'),
        errorColorPressed: cssVar('--dis-color-surface-error-pressed'),
        textColorBase: cssVar('--dis-color-text-body'),
        textColor1: cssVar('--dis-color-text-body'),
        textColor2: cssVar('--dis-color-text-body'),
        textColor3: cssVar('--dis-color-text-body'),
        textColorDisabled: cssVar('--dis-color-text-disabled'),
        placeholderColor: cssVar('--dis-color-text-placeholder'),
        placeholderColorDisabled: cssVar('--dis-color-text-placeholder'),
        iconColor: cssVar('--dis-color-icon-primary'),
        iconColorHover: cssVar('--dis-color-icon-primary-hover'),
        iconColorPressed: cssVar('--dis-color-icon-primary-pressed'),
        iconColorDisabled: cssVar('--dis-color-icon-primarty-disabled'),
        dividerColor: cssVar('--dis-color-line-primary'),
        borderColor: cssVar('--dis-color-line-primary'),
        popoverColor: cssVar('--dis-color-surface-primary'),
        boxShadow1: cssVar('--dis-shadow-boxshadow1'),
        boxShadow2: cssVar('--dis-shadow-boxshadow2'),
        boxShadow3: cssVar('--dis-shadow-boxshadow3'),

        // Font
        fontFamily: cssVar('--dis-font-family-regular'),
        fontFamilyMono: cssVar('--dis-font-family-mono'),
        fontWeight: cssVar('--dis-font-weight-regular'),
        fontWeightStrong: cssVar('--dis-font-weight-strong'),
        fontSize: cssVar('--dis-font-size-medium'),
        fontSizeMini: cssVar('--dis-font-size-mini'),
        fontSizeTiny: cssVar('--dis-font-size-tiny'),
        fontSizeSmall: cssVar('--dis-font-size-small'),
        fontSizeMedium: cssVar('--dis-font-size-medium'),
        fontSizeLarge: cssVar('--dis-font-size-large'),
        fontSizeHuge: cssVar('--dis-font-size-huge'),

        // Shape
        borderRadius: cssVar('--dis-shape-radius-medium'),
        borderRadiusSmall: cssVar('--dis-shape-radius-small'),
        heightMini: cssVar('--dis-shape-height-2_0x'),
        heightTiny: cssVar('--dis-shape-height-3_0x'),
        heightSmall: cssVar('--dis-shape-height-4_0x'),
        heightMedium: cssVar('--dis-shape-height-5_0x'),
        heightLarge: cssVar('--dis-shape-height-6_0x'),
        heightHuge: cssVar('--dis-shape-height-7_0x'),
    },

    Checkbox: {
        sizeMedium: cssVar('--dis-shape-height-checkbox'),
        color: cssVar('--dis-color-surface-secondary'),
        checkMarkColor: cssVar('--dis-color-icon-inverted'),
        labelLineHeight: cssVar('--dis-font-lineheight-medium'),
    },

    Form: {
        labelPaddingVertical: `0 0 ${cssVar('--dis-space-1_0x')} 2px`,
        labelTextColor: cssVar('--dis-color-text-label'),
        asteriskColor: cssVar('--dis-color-text-label'),
        labelFontSizeTopSmall: cssVar('--dis-font-size-small'),
        labelFontSizeTopMedium: cssVar('--dis-font-size-small'),
        labelFontSizeTopLarge: cssVar('--dis-font-size-small'),
        labelHeightSmall: cssVar('--dis-font-lineheight-small'),
        labelHeightMedium: cssVar('--dis-font-lineheight-small'),
        labelHeightLarge: cssVar('--dis-font-lineheight-small'),
        labelFontWeight: cssVar('--dis-font-weight-strong'),
        feedbackPadding: `${cssVar('--dis-space-1_0x')} 0 0 0`,
        feedbackTextColor: cssVar('--dis-color-text-helper'),
        feedbackTextColorWarning: cssVar('--dis-color-text-warning'),
        feedbackTextColorError: cssVar('--dis-color-text-error'),
        feedbackFontSizeSmall: cssVar('--dis-font-size-small'),
        feedbackFontSizeMedium: cssVar('--dis-font-size-small'),
        feedbackFontSizeLarge: cssVar('--dis-font-size-small'),
    },

    Space: {
        gapSmall: cssVar('--dis-space-1_0x'),
        gapMedium: cssVar('--dis-space-2_0x'),
        gapLarge: cssVar('--dis-space-3_0x'),
    },

    Typography: {
        headerTextColor: cssVar('--dis-color-text-title'),
        headerFontSize1: cssVar('--dis-font-size-h1'),
        headerFontSize2: cssVar('--dis-font-size-h2'),
        headerFontSize3: cssVar('--dis-font-size-h3'),
        headerFontSize4: cssVar('--dis-font-size-h4'),
        headerFontSize5: cssVar('--dis-font-size-h5'),
        headerFontSize6: cssVar('--dis-font-size-h6'),
    },

    Alert: {
        color: cssVar('--dis-color-neutral-100'),
        border: 0,
        colorInfo: cssVar('--dis-color-semantic-blue-100'),
        borderInfo: 0,
        colorSuccess: cssVar('--dis-color-semantic-green-100'),
        borderSuccess: 0,
        colorWarning: cssVar('--dis-color-semantic-orange-100'),
        borderWarning: 0,
        colorError: cssVar('--dis-color-semantic-red-100'),
        borderError: 0,
        titleFontWeight: cssVar('--dis-font-weight-strong'),
        padding: cssVar('--dis-space-1_5x'),
        iconColor: cssVar('--dis-color-icon-primary'),
        iconMargin: `${cssVar('--dis-space-1_5x')} ${cssVar('--dis-space-1_5x')} 0`,
        closeIconColor: cssVar('--dis-color-icon-primary'),
        closeMargin: `${cssVar('--dis-space-1_5x')} ${cssVar('--dis-space-1_5x')} 0 0`,
    },

    Button: {
        border: `1px solid ${cssVar('--dis-color-line-primary')}`,
        textColorTertiary: cssVar('--dis-color-text-label'),
        fontWeight: cssVar('--dis-font-weight-strong'),
        fontWeightStrong: cssVar('--dis-font-weight-strong'),
        fontSizeTiny: cssVar('--dis-font-size-small'),
        fontSizeSmall: cssVar('--dis-font-size-medium'),
        fontSizeMedium: cssVar('--dis-font-size-medium'),
        fontSizeLarge: cssVar('--dis-font-size-large'),
        paddingTiny: `0 ${cssVar('--dis-space-0_5x')}`,
        paddingSmall: `0 ${cssVar('--dis-space-1_0x')}`,
        paddingMedium: `0 ${cssVar('--dis-space-1_5x')}`,
        paddingLarge: `0 ${cssVar('--dis-space-1_5x')}`,
        paddingRoundTiny: `0 ${cssVar('--dis-space-0_5x')}`,
        paddingRoundSmall: `0 ${cssVar('--dis-space-1_0x')}`,
        paddingRoundMedium: `0 ${cssVar('--dis-space-1_5x')}`,
        paddingRoundLarge: `0 ${cssVar('--dis-space-1_5x')}`,
        iconMarginTiny: 0,
        iconMarginSmall: cssVar('--dis-space-0_5x'),
        iconMarginMedium: cssVar('--dis-space-0_5x'),
        iconMarginLarge: cssVar('--dis-space-0_5x'),
        iconSizeTiny: '12px',
        iconSizeSmall: cssVar('--dis-shape-height-2_0x'),
        iconSizeMedium: cssVar('--dis-shape-height-2_0x'),
        iconSizeLarge: cssVar('--dis-shape-height-2_5x'),
    },

    Card: {
        titleFontSizeSmall: cssVar('--dis-font-size-h3'),
        titleFontSizeMedium: cssVar('--dis-font-size-h2'),
        titleFontSizeLarge: cssVar('--dis-font-size-h1'),
        titleFontSizeHuge: cssVar('--dis-font-size-h1'),
        paddingSmall: `${cssVar('--dis-space-1_5x')} ${cssVar('--dis-space-2_0x')}`,
        paddingMedium: `${cssVar('--dis-space-2_0x')} ${cssVar('--dis-space-4_0x')}`,
        paddingLarge: `${cssVar('--dis-space-2_0x')} ${cssVar('--dis-space-4_0x')}`,
        paddingHuge: `${cssVar('--dis-space-3_0x')} ${cssVar('--dis-space-4_0x')}`,
        closeIconColor: cssVar('--dis-color-icon-primary'),
        actionColor: cssVar('--dis-color-surface-primary'),
    },

    Dialog: {
        actionSpace: cssVar('--dis-space-2_0x'),
        titleFontSize: cssVar('--dis-font-size-h3'),
        padding: cssVar('--dis-space-3_0x'),
        closeIconColor: cssVar('--dis-color-icon-primary'),
        closeMargin: `${cssVar('--dis-space-3_0x')} ${cssVar('--dis-space-3_0x')} 0 0`
    },

    Input: {
        border: cssVar('--dis-input-border'),
        colorFocus: cssVar('--dis-color-surface-primary'),
        borderFocus: cssVar('--dis-color-action-primary-hover'),
        boxShadowFocus: cssVar('--dis-focus-boxshadowfocus'),
        colorFocusWarning: cssVar('--dis-color-surface-primary'),
        borderFocusWarning: cssVar('--dis-color-surface-warning'),
        boxShadowFocusWarning: cssVar('--dis-focus-boxshadowfocuswarning'),
        colorFocusError: cssVar('--dis-color-surface-primary'),
        borderFocusError: cssVar('--dis-color-surface-error'),
        boxShadowFocusError: cssVar('--dis-focus-boxshadowfocuserror'),
        fontSizeTiny: cssVar('--dis-font-size-small'),
        fontSizeSmall: cssVar('--dis-font-size-medium'),
        fontSizeMedium: cssVar('--dis-font-size-medium'),
        fontSizeLarge: cssVar('--dis-font-size-large'),
    },

    Select: {
        peers: {
            InternalSelectMenu: {
                paddingMedium: cssVar('--dis-space-1_0x'),
                optionHeightMedium: '36px',
                optionPaddingMedium: `0 ${cssVar('--dis-space-2_0x')} 0 ${cssVar('--dis-space-1_0x')}`,
                optionFontSizeLarge: cssVar('--dis-font-size-large'),
                optionHeightLarge: cssVar('--dis-shape-height-5_0x'),
                optionFontSizeHuge: cssVar('--dis-font-size-large'),
                optionHeightHuge: cssVar('--dis-shape-height-6_0x'),
                optionColorPending: cssVar('--dis-color-surface-base'),
                optionColorActive: cssVar('--dis-color-surface-base'),
            },
        },
    },

    Pagination: {
        buttonBorder: `1px solid ${cssVar('--dis-color-line-primary')}`,
        buttonBorderHover: `1px solid ${cssVar('--dis-color-line-primary-hover')}`,
        buttonIconColorHover: cssVar('--dis-color-text-link'),
        itemTextColor: cssVar('--dis-color-text-body'),
        itemTextColorHover: cssVar('--dis-color-text-link'),
        itemTextColorActive: cssVar('--dis-color-text-link'),
        itemBorderActive: `1px solid ${cssVar('--dis-color-line-primary-active')}`,
        itemFontSizeMedium: cssVar('--dis-font-size-large'),
        jumperFontSizeMedium: '16px',
        itemTextColorDisabled: cssVar('--dis-color-icon-primary-disabled'),
        itemColorDisabled: cssVar('--dis-color-surface-disabled'),
        itemBorderDisabled: cssVar('--dis-color-surface-disabled'),
        jumperTextColorDisabled: cssVar('--dis-color-icon-primary-disabled'),
    },

    Modal: {
        boxShadow: cssVar('--dis-shadow-boxshadow3'),
    },

    Tag: {
        fontWeightStrong: cssVar('--dis-font-weight-strong'),
        padding: `0 ${cssVar('--dis-space-1_0x')}`,
        borderRadius: '100px',
        
        fontSizeSmall: cssVar('--dis-font-size-small'),
        heightSmall: cssVar('--dis-shape-height-2_5x'),

        fontSizeMediun: cssVar('--dis-font-size-small'),
        heightMedium: cssVar('--dis-shape-height-3_0x'),
        
        fontSizeLarge: cssVar('--dis-font-size-medium'),
        heightLarge: cssVar('--dis-shape-height-4_0x'),

        border: '0',
        textColor: cssVar('--dis-color-neutral-600'),
        color: cssVar('--dis-color-neutral-100'),
        colorBordered: cssVar('--dis-color-neutral-100'),
        closeIconColor: cssVar('--dis-color-neutral-500'),
        closeIconColorHover: cssVar('--dis-color-neutral-600'),
        closeIconColorPressed: cssVar('--dis-color-neutral-700'),
        closeColorHover: 'transparent',
        closeColorPressed: 'transparent',

        borderPrimary: '0',
        textColorPrimary: cssVar('--dis-color-primary-600'),
        colorPrimary: cssVar('--dis-color-primary-100'),
        colorBorderedPrimary: cssVar('--dis-color-primary-100'),
        closeIconColorPrimary: cssVar('--dis-color-primary-500'),
        closeIconColorHoverPrimary: cssVar('--dis-color-primary-600'),
        closeIconColorPressedPrimary: cssVar('--dis-color-primary-700'),
        closeColorHoverPrimary: 'transparent',
        closeColorPressedPrimary: 'transparent',

        borderInfo: '0',
        textColorInfo: cssVar('--dis-color-semantic-blue-600'),
        colorInfo: cssVar('--dis-color-semantic-blue-100'),
        colorBorderedInfo: cssVar('--dis-color-semantic-blue-100'),
        closeIconColorInfo: cssVar('--dis-color-semantic-blue-500'),
        closeIconColorHoverInfo: cssVar('--dis-color-semantic-blue-600'),
        closeIconColorPressedInfo: cssVar('--dis-color-semantic-blue-700'),
        closeColorHoverInfo: 'transparent',
        closeColorPressedInfo: 'transparent',
        
        borderSuccess: '0',
        textColorSuccess: cssVar('--dis-color-semantic-green-600'),
        colorSuccess: cssVar('--dis-color-semantic-green-100'),
        colorBorderedSuccess: cssVar('--dis-color-semantic-green-100'),
        closeIconColorSuccess: cssVar('--dis-color-semantic-green-500'),
        closeIconColorHoverSuccess: cssVar('--dis-color-semantic-green-600'),
        closeIconColorPressedSuccess: cssVar('--dis-color-semantic-green-700'),
        closeColorHoverSuccess: 'transparent',
        closeColorPressedSuccess: 'transparent',

        borderWarning: '0',
        textColorWarning: cssVar('--dis-color-semantic-orange-600'),
        colorWarning: cssVar('--dis-color-semantic-orange-100'),
        colorBorderedWarning: cssVar('--dis-color-semantic-orange-100'),
        closeIconColorWarning: cssVar('--dis-color-semantic-orange-500'),
        closeIconColorHoverWarning: cssVar('--dis-color-semantic-orange-600'),
        closeIconColorPressedWarning: cssVar('--dis-color-semantic-orange-700'),
        closeColorHoverWarning: 'transparent',
        closeColorPressedWarning: 'transparent',

        borderError: '0',
        textColorError: cssVar('--dis-color-semantic-red-600'),
        colorError: cssVar('--dis-color-semantic-red-100'),
        colorBorderedError: cssVar('--dis-color-semantic-red-100'),
        closeIconColorError: cssVar('--dis-color-semantic-red-500'),
        closeIconColorHoverError: cssVar('--dis-color-semantic-red-600'),
        closeIconColorPressedError: cssVar('--dis-color-semantic-red-700'),
        closeColorHoverError: 'transparent',
        closeColorPressedError: 'transparent',
    },

    Message: {
        fontSize: cssVar('--dis-font-size-large'),
        padding: `${cssVar('--dis-space-1_5x')} ${cssVar('--dis-space-3_0x')} ${cssVar('--dis-space-1_5x')} ${cssVar('--dis-space-2_0x')}`,
        boxShadow: cssVar('--dis-shadow-boxshadow3'),
        boxShadowInfo: cssVar('--dis-shadow-boxshadow3'),
        boxShadowSuccess: cssVar('--dis-shadow-boxshadow3'),
        boxShadowWarning: cssVar('--dis-shadow-boxshadow3'),
        boxShadowError: cssVar('--dis-shadow-boxshadow3'),
        boxShadowLoading: cssVar('--dis-shadow-boxshadow3'),
    },

    Notification: {
        boxShadow: cssVar('--dis-shadow-boxshadow3'),
        padding: cssVar('--dis-space-1_5x'),
        titleFontSize: cssVar('--dis-font-size-h3'),
        fontSize: cssVar('--dis-font-size-medium'),
    },

    Tooltip: {
        color: cssVar('--dis-color-surface-inverted-secondary'),
        textColor: cssVar('--dis-color-text-inverted'),
        padding: `${cssVar('--dis-space-1_0x')} ${cssVar('--dis-space-1_5x')}`,
        borderRadius: cssVar('--dis-shape-radius-medium'),
    },

    Tabs: {
        tabTextColorLine: cssVar('--dis-color-text-label'),
        tabTextColorActiveLine: cssVar('--dis-color-text-link-pressed'),
        tabTextColorHoverLine: cssVar('--dis-color-text-body'),
        tabTextColorDisabledLine: cssVar('--dis-color-text-disabled'),
        tabFontWeightActive: cssVar('--dis-font-weight-strong'),
        fontWeightStrong: cssVar('--dis-font-weight-strong'),
        tabBorderColor: cssVar('--dis-color-line-primary'),
        barColor: cssVar('--dis-color-line-primary-active'),
        tabPaddingMediumLine: `10px ${cssVar('--dis-space-1_0x')}`,
        tabGapMediumLine: cssVar('--dis-space-2_0x'),
        panePaddingMedium: `${cssVar('--dis-space-2_0x')} 0 0 0`
    },

    Anchor: {
        linkPadding: `0 0 0 ${cssVar('--dis-space-2_0x')}`,
        railWidth: cssVar('--dis-space-0_5x'),
        railColor: cssVar('--dis-color-line-primary'),
        borderRadius: cssVar('--dis-shape-radius-medium'),
        linkFontSize: cssVar('--dis-font-size-medium'),
        linkTextColor: cssVar('--dis-color-text-label'),
        linkTextColorHover: cssVar('--dis-color-text-link-hover'),
        linkTextColorPressed: cssVar('--dis-color-text-link-pressed'),
        linkTextColorActive: cssVar('--dis-color-text-link')
    },

    Radio: {
        fontSizeSmall: cssVar('--dis-font-size-medium')
    }
};